/**
 * Sidebar Navigation Component
 *
 * Collapsible sidebar with logo, grouped nav sections,
 * and bottom collapse toggle.
 */

import { Fragment, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "@tanstack/react-router";
import { ArrowLineLeft, ArrowLineRight } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { Logo } from "../icons/Logo";
import { Favicon } from "../icons/Favicon";
import { navSections, type NavItem } from "./navConfig";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const SIDEBAR_WIDTH = {
  collapsed: 73,
  expanded: 163,
} as const;
const SIDEBAR_NAV_ICON_SIZE = 18.2;
const SIDEBAR_FAVICON_SIZE = 27.5;
const COLLAPSED_NAV_ICON_LEFT = 11.4;
const COLLAPSED_FAVICON_LEFT = 7.75;

function SidebarNavItem({ item, isActive, collapsed }: { item: NavItem; isActive: boolean; collapsed: boolean }) {
  const ItemIcon = item.icon;
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const [hovered, setHovered] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const showTooltip = useCallback(() => {
    if (!collapsed || !linkRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
    setHovered(true);
  }, [collapsed]);

  const hideTooltip = useCallback(() => setHovered(false), []);

  return (
    <li className="relative cursor-pointer">
      {isActive && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute inset-0 rounded-lg bg-background border border-input shadow-xs pointer-events-none"
          transition={{ type: "spring", stiffness: 350, damping: 30 }}
        />
      )}
      <Link
        ref={linkRef}
        to={item.to}
        className="relative z-10 block rounded-lg text-sm font-medium text-foreground hover:bg-background hover:shadow-xs border border-transparent hover:border-input transition-all duration-150 cursor-pointer"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <motion.div
          className="flex items-center py-2.5 cursor-pointer"
          animate={{ paddingLeft: collapsed ? COLLAPSED_NAV_ICON_LEFT : 11, paddingRight: collapsed ? 0 : 12 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <ItemIcon size={SIDEBAR_NAV_ICON_SIZE} className="shrink-0 text-foreground" />
          <motion.span
            initial={false}
            animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 7 }}
            transition={{ duration: collapsed ? 0.15 : 0.3, delay: collapsed ? 0 : 0.15, ease: "easeInOut" }}
            className="whitespace-nowrap"
          >
            {item.label}
          </motion.span>
        </motion.div>
      </Link>
      {tooltipPos && createPortal(
        <div
          className={`fixed z-[100] pointer-events-none -translate-y-1/2 transition-all duration-150 ${hovered ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {/* Arrow */}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-background border-l border-b border-input rotate-45 z-10" />
          {/* Label */}
          <div className="relative bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
            {item.label}
          </div>
        </div>,
        document.getElementById("authenticated-root") ?? document.body,
      )}
    </li>
  );
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const [toggleTooltipPos, setToggleTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const [toggleHovered, setToggleHovered] = useState(false);
  const [toggleFocused, setToggleFocused] = useState(false);
  const toggleLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";
  const contentGutter = collapsed ? 15 : 16;
  const contentGutterStyle = {
    paddingLeft: contentGutter,
    paddingRight: contentGutter,
  };

  const positionToggleTooltip = useCallback(() => {
    if (!collapsed || !toggleButtonRef.current) return false;
    const rect = toggleButtonRef.current.getBoundingClientRect();
    setToggleTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
    return true;
  }, [collapsed]);

  const showToggleTooltipOnHover = useCallback(() => {
    if (positionToggleTooltip()) setToggleHovered(true);
  }, [positionToggleTooltip]);

  const showToggleTooltipOnFocus = useCallback(() => {
    if (positionToggleTooltip()) setToggleFocused(true);
  }, [positionToggleTooltip]);

  return (
    <motion.aside
      animate={{ width: collapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="flex flex-col shrink-0 bg-background overflow-y-hidden overflow-x-visible"
    >
      {/* Brand Header — favicon when collapsed, full logo when expanded */}
      <motion.div
        animate={contentGutterStyle}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="shrink-0 pt-5 pb-1"
      >
        <div className="relative h-12">
          <motion.div
            initial={false}
            animate={{ opacity: collapsed ? 1 : 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute inset-y-0 flex items-center"
            style={{ left: COLLAPSED_FAVICON_LEFT }}
            aria-hidden={!collapsed}
          >
            <Favicon size={SIDEBAR_FAVICON_SIZE} />
          </motion.div>
          <motion.div
            initial={false}
            animate={{ opacity: collapsed ? 0 : 1 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute inset-y-0 left-[-3px] flex items-center"
            aria-hidden={collapsed}
          >
            <Logo height={58} />
          </motion.div>
        </div>
      </motion.div>

      {/* Navigation Sections */}
      <motion.nav
        animate={{ paddingTop: collapsed ? 24 : 14, ...contentGutterStyle }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="flex-1 overflow-y-auto [&_a]:!cursor-pointer [&_li]:!cursor-pointer [&_a_*]:!cursor-pointer"
      >
        {navSections.map((section, idx) => (
          <Fragment key={section.label}>
          <div className="mb-4">
            <motion.div
              animate={{ height: collapsed ? 0 : "auto", opacity: collapsed ? 0 : 1 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-2 pt-3 text-[11px] font-semibold tracking-wider text-foreground/70 uppercase">
                {section.label}
              </div>
            </motion.div>
            <ul className="space-y-[4px]">
              {section.items.map((item) => (
                <SidebarNavItem
                  key={item.to}
                  item={item}
                  isActive={currentPath.startsWith(item.to)}
                  collapsed={collapsed}
                />
              ))}
            </ul>
          </div>
          {idx < navSections.length - 1 && <div className="mx-2 mb-4" style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }} />}
          </Fragment>
        ))}
      </motion.nav>

      {/* Collapse Toggle */}
      <motion.div
        animate={contentGutterStyle}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="shrink-0 pb-3"
      >
        <div
          role="separator"
          aria-orientation="horizontal"
          className="mx-2 mb-3"
          style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }}
        />
        <button
          ref={toggleButtonRef}
          type="button"
          onClick={onToggle}
          onMouseEnter={showToggleTooltipOnHover}
          onMouseLeave={() => setToggleHovered(false)}
          onFocus={showToggleTooltipOnFocus}
          onBlur={() => setToggleFocused(false)}
          aria-label={toggleLabel}
          className="flex items-center w-full rounded-lg text-sm font-medium text-foreground hover:bg-background hover:shadow-xs border border-transparent hover:border-input transition-all duration-150 cursor-pointer"
        >
          <motion.div
            className="flex items-center py-2.5 cursor-pointer"
            animate={{ paddingLeft: collapsed ? COLLAPSED_NAV_ICON_LEFT : 11, paddingRight: collapsed ? 0 : 12 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {collapsed ? (
              <ArrowLineRight size={SIDEBAR_NAV_ICON_SIZE} className="shrink-0 text-foreground" />
            ) : (
              <ArrowLineLeft size={SIDEBAR_NAV_ICON_SIZE} className="shrink-0 text-foreground" />
            )}
            <motion.span
              initial={false}
              animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 7 }}
              transition={{ duration: collapsed ? 0.15 : 0.3, delay: collapsed ? 0 : 0.15, ease: "easeInOut" }}
              className="whitespace-nowrap text-sm font-medium"
            >
              Collapse
            </motion.span>
          </motion.div>
        </button>
        {collapsed && toggleTooltipPos && createPortal(
          <div
            className={`fixed z-[100] pointer-events-none -translate-y-1/2 transition-all duration-150 ${toggleHovered || toggleFocused ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
            style={{ top: toggleTooltipPos.top, left: toggleTooltipPos.left }}
          >
            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-background border-l border-b border-input rotate-45 z-10" />
            <div className="relative bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
              {toggleLabel}
            </div>
          </div>,
          document.getElementById("authenticated-root") ?? document.body,
        )}
      </motion.div>
    </motion.aside>
  );
}
